import { createContext, useContext } from 'react';

/**
 * Controls how a compact care-plan row's secondary lines (instructions, dose, schedule) unfurl. By
 * default (`controlled: false`) they unfurl on card hover/focus — the legacy board and patient-web
 * behaviour. When a host opts into `controlled: true`, hover is disabled and the lines are
 * shown/hidden entirely by `expanded`, driven by the board's "show details" toggle.
 */
type CarePlanRowExpansion = {
  controlled: boolean;
  expanded: boolean;
  /**
   * Hold every row's title to one line, so each item takes the same height.
   *
   * The plan table's collapsed grid reads as slots — one item, one slot — which only works when a long
   * title takes the same room as a short one. Elsewhere titles wrap to two lines, as they always have.
   */
  singleLine?: boolean;
};

export const CarePlanRowExpansionContext = createContext<CarePlanRowExpansion>({
  controlled: false,
  expanded: false,
});

export const useCarePlanRowExpansion = (): CarePlanRowExpansion => useContext(CarePlanRowExpansionContext);

/** A row title's clamp: one line inside a slotted grid, two everywhere else. */
export const useCarePlanTitleClamp = (): string =>
  useCarePlanRowExpansion().singleLine ? 'line-clamp-1' : 'line-clamp-2';
